import {Injectable} from '@angular/core';
import {SettingTypeEnum} from './setting-type.enum';
import {Setting} from './setting';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {SettingsApiService} from './settings-api.service';
import {Project} from '@mydocma/project';
import {SettingNamesEnum} from './setting-names.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {executeWithTimeout} from '@docma-mm-core/rx/rx-functions';

const dbgPrint = false;
const dbgPrint_Settings = dbgPrint && true;

@Injectable()
export class SettingsService {
  constructor(private readonly settingsApiService: SettingsApiService,
              private readonly personalizationService: PersonalizationService) {
  }

  async save(value: string, keyIDB: PersonalizationKeysEnum, keySettings: SettingNamesEnum, project: Project) {
    const valueObj = {value: value, modifiedOn: Date.now()};

    const foundSetting = await executeWithTimeout<Setting>(this.settingsApiService.get((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings), `Loading of userproject setting '${keySettings}' timed out!`, 5000);
    if (!foundSetting?.id) {
      await executeWithTimeout<Setting>(this.settingsApiService.create((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings, JSON.stringify(valueObj)), `Creating user setting '${keySettings}' timed out!`, 5000);
    }
    else {
      await executeWithTimeout<Setting>(this.settingsApiService.update((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings, JSON.stringify(valueObj)), `Updating user setting '${keySettings}' timed out!`, 5000);
    }

    if (project) {
      await this.personalizationService.setProjectValue(project.id, keyIDB, JSON.stringify(valueObj));
    }
    else {
      await this.personalizationService.setValue(keyIDB, JSON.stringify(valueObj));
    }
  }

  async delete (keyIDB: PersonalizationKeysEnum, keySettings: SettingNamesEnum, project: Project) {
    await this.settingsApiService.delete((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings);  // (project ? `${project.id}.${keySettings}` : keySettings)
    await this.personalizationService.deleteValue((project ? `${project.id}.${keyIDB}` : keyIDB));
  }

  async deleteAll (keySettings: SettingNamesEnum, project: Project) {
    await this.settingsApiService.deleteAll((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings);
  }


  async load(keyIDB: PersonalizationKeysEnum, keySettings: SettingNamesEnum, project: Project) {
    let valueIDB: any;
    let valueSettings: Setting;

    if (project) {
      valueIDB = await this.personalizationService.getProjectValue(project.id, keyIDB);

      valueSettings = await executeWithTimeout<Setting>(this.settingsApiService.get(SettingTypeEnum.userproject, keySettings),
        `Loading of userproject setting '${keySettings}' timed out!`,
        5000);
    }
    else {
      valueIDB = await this.personalizationService.getValue(keyIDB);

      valueSettings = await executeWithTimeout<Setting>(this.settingsApiService.get(SettingTypeEnum.user, keySettings),
        `Loading of user setting '${keySettings}' timed out!`,
        5000);
    }

    const value1 = (valueIDB && valueIDB !== '' && !(valueIDB instanceof Object && Object.keys(valueIDB).length === 0) ? JSON.parse(valueIDB) : null);
    const value2 = (valueSettings && valueSettings.value !== '' && !(valueSettings instanceof Object && Object.keys(valueSettings?.value).length === 0) ? JSON.parse(valueSettings.value) : null);

    const storeIDBFunc = async (value) => (project ?
      await this.personalizationService.setProjectValue(project.id, keyIDB, value) :
      await this.personalizationService.setValue(keyIDB, value));
    const storeSettingFunc = async (value) => (valueSettings?.id ?
      executeWithTimeout<Setting>(this.settingsApiService.update((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings, value), `Creating user setting '${keySettings}' timed out!`, 5000) :
      executeWithTimeout<Setting>(this.settingsApiService.create((project ? SettingTypeEnum.userproject : SettingTypeEnum.user), keySettings, value), `Updating user setting '${keySettings}' timed out!`, 5000) )

      // , `Creating user setting '${keySettings}' timed out!`, 5000)
    const d1: Date = value1?.modifiedOn;
    const d2: Date = value2?.modifiedOn;

    if (d1 && d2) {
      if (d1 > d2) {
        // take d1 & store to d2
        await storeSettingFunc(value1);
        return value1.value;
      }
      else if (d1 < d2) {
        // take d2 & store to d1
        await storeIDBFunc(JSON.stringify(value2));
        return value2.value;
      }
      else {
        // take d1
        return value1.value;
      }
    }
    else if (!d1 && d2) {
      // take d2 & store to d1
      await storeIDBFunc(JSON.stringify(value2));
      return value2.value;
    }
    else if (d1 && !d2) {
      // take d1 & store to d2
      await storeSettingFunc(JSON.stringify(value1));
      return value1.value;
    }
    else {
      // NO modifiedOn existing...
      if (value1) {
        // take d1 & store to d1 + d2
        const value = JSON.stringify({value: value1, modifiedOn: Date.now()});
        await storeIDBFunc(value);
        await storeSettingFunc(value);
        return value1;
      }
      else if (value2) {
        // take d2 & store to d1 + d2
        const value = JSON.stringify({value: value2, modifiedOn: Date.now()});
        await storeIDBFunc(value);
        await storeSettingFunc(value);
        return value2;
      }
      else {
        // NO VALUE EXISTING!
        return null;
      }
    }
  }
}


export async function GetValue<T>(value: any, defaultValue?: T){
  if (value && value != '' && !(value instanceof Object && Object.keys(value).length === 0)) {
    let parsedValue: T;

    try {
      if (dbgPrint_Settings) {
        console.warn('value');
        console.warn(value);
      }

      parsedValue = JSON.parse(value);

      if (dbgPrint_Settings) {
        console.warn('parsedValue');
        console.warn(parsedValue);
      }

      return parsedValue;
    }
    catch (err) {
      if (dbgPrint_Settings) {
        console.warn('err');
        console.warn(err);
      }
      return defaultValue;
    }
  }
  else {
    return defaultValue;
  }
}


